import type { MembershipInstanceLine } from '@kalos/kalos-rpc';

/** One add-on as it was sold, ready to list on a read-only panel. */
export interface InstanceLineAddon {
  /** The instance-line row this came from — a stable React key, unlike the name. */
  id: number;
  name: string;
  /** What the customer was charged: unit price times quantity, so never a catalog unit price. */
  cost: number;
  /** The catalog add-on this points at, for looking up today's price. */
  membershipLineId: number;
}

const FK_NAMED = /^\d+$/;

/**
 * The add-ons a membership was actually sold, out of its frozen instance lines.
 *
 * Residential rows have two writers: the backend snapshots every add-on the plan is eligible for
 * when the membership is created, and enrollment adds a second row per add-on the customer chose,
 * named with the raw membership_line id. So a line id carrying more than one row is a real sale,
 * and a single row is only eligibility. Commercial plans are priced on their lines instead, where
 * the first row is the base rate and the rest are the add-ons.
 */
export const selectedInstanceAddons = (
  lines: MembershipInstanceLine[],
  isCommercial: boolean,
): InstanceLineAddon[] => {
  if (isCommercial) {
    return lines.slice(1).map((line) => ({
      id: line.id,
      name: line.name,
      cost: line.cost,
      membershipLineId: line.membershipLineId,
    }));
  }

  const byLineId = new Map<number, MembershipInstanceLine[]>();
  for (const line of lines) {
    if (line.membershipLineId <= 0) continue;
    const group = byLineId.get(line.membershipLineId);
    if (group) group.push(line);
    else byLineId.set(line.membershipLineId, [line]);
  }

  const addons: InstanceLineAddon[] = [];
  for (const [membershipLineId, group] of byLineId) {
    if (group.length < 2) continue;
    const catalogNamed = group.find((line) => !FK_NAMED.test(line.name.trim()));
    // The enrollment row is written after the snapshot, so the highest id carries any price override.
    const priced = group.reduce((latest, line) => (line.id > latest.id ? line : latest));
    addons.push({
      id: priced.id,
      name: catalogNamed?.name ?? `Add-on #${membershipLineId}`,
      cost: priced.cost,
      membershipLineId,
    });
  }
  return addons;
};

/** The plan's own price as frozen at enrollment; undefined for memberships recorded before it existed. */
export const frozenBaseCost = (lines: MembershipInstanceLine[]): number | undefined =>
  lines.find((line) => line.membershipLineId <= 0 && line.sortOrder === 0 && line.cost > 0)?.cost;
